import React, { FC, useState } from 'react'
import clsx from 'clsx'
import type { Topic } from '@pepper/utils/src/types/seo'
import {
  useContentRefreshDomains,
  useGetProjectDomain,
  useMessage,
} from '@pepper/ui'

import SEOTable from 'container/SEOTools/components/SEOTable/SEOTable'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useKeywordLabelling } from 'utils/hooks/IdeaLab/useKeywordLabelling'
import { getColumns } from './columns'

import s from './Table.module.css'

type Props = {
  isDomainRemoved: boolean
  projectId: string
  loading: boolean
  totalRecords: number
  onSelect: (keys: React.Key[]) => void
  onReportClick: (topic: Topic) => void
  onPageChange: (page: number) => void
  rowsSelected: React.Key[]
  onCreateOrderClick: (topic: Topic) => void
  onCreatePrivateDocClick: (topic: Topic) => void
  onEditTopic: (topic: Topic) => void
  onCreateReport: (topic: Topic) => void
  onDeleteTopic: (topic: Topic) => void
  onViewItemClick: (
    itemId: string,
    itemType: 'private_doc' | 'assignment'
  ) => void
  page: number
  total: number
  onPageSizeChange: (limit: number) => void
  dataSource?: Topic[]
  limit?: number
}

export const Table: FC<Props> = ({
  projectId,
  dataSource,
  loading,
  totalRecords,
  onSelect,
  onReportClick,
  onPageChange,
  rowsSelected,
  onCreateOrderClick,
  onCreatePrivateDocClick,
  onViewItemClick,
  onEditTopic,
  onCreateReport,
  onDeleteTopic,
  page,
  total,
  limit,
  onPageSizeChange,
  isDomainRemoved,
}) => {
  const { showError } = useMessage()
  const companyId = useSelectedCompanyId()
  const [selectedSuggestionId, setSelectedSuggestionId] = useState('')
  const { data: projectDomain } = useGetProjectDomain({
    data: {
      companyId,
      projectId,
    },
  })

  const {
    data: keywordLabelling,
    isLoading: isLoadingKeywordLabel,
    error: labellingError,
  } = useKeywordLabelling({
    id: selectedSuggestionId,
    enabled: selectedSuggestionId.length > 0 && !!companyId,
    companyId,
  })

  const getRowKey = (row: Topic) => {
    return row.id
  }

  const { isGoogleConnected } = useContentRefreshDomains(companyId)

  if (labellingError) {
    showError(labellingError)
  }

  return (
    <div className={s.table}>
      <SEOTable
        rowKey={getRowKey}
        className={clsx({
          [s.hasSelectedRows]: rowsSelected.length,
        })}
        loading={loading}
        columns={getColumns({
          totalRecords,
          onCreateOrderClick,
          onCreatePrivateDocClick,
          onReportClick,
          onViewItemClick,
          onEditTopic,
          onCreateReport,
          onDeleteTopic,
          isLoadingKeywordLabel,
          onGetKeywordLabel: (keywordSuggestionId: string) => {
            setSelectedSuggestionId(keywordSuggestionId)
          },
          pageRankingResults: keywordLabelling,
          domain: projectDomain?.domain.domain_url,
          isGoogleConnected,
          companyId,
          projectId,
          isDomainRemoved,
        })}
        dataSource={dataSource}
        rowSelection={{
          type: 'checkbox',
          onChange: onSelect,
          selectedRowKeys: rowsSelected,
          getCheckboxProps: record => ({
            disabled:
              record?.crr &&
              ['pending', 'processing'].includes(
                record?.crr?.generation_status_name
              ),
          }),
        }}
        rowClassName={s.row}
        pagination={{
          hideOnSinglePage: true,
          onShowSizeChange: (_, size) => onPageSizeChange(size),
          onChange: onPageChange,
          pageSizeOptions: ['10', '25', '50', '100'],
          total,
          current: page,
          showSizeChanger: true,
          pageSize: limit,
          showTotal: (_total, range) =>
            `Showing ${range[0]} to ${range[1]} of ${_total} topics`,
        }}
      />
    </div>
  )
}
